/**
 * Overlay primitives — design/09 §8: tooltip, popover, modal, drawer, toast.
 *
 * §6: "Elevation: hairlines and surface steps, not drop shadows. ONE shadow token
 * exists, for overlays and popovers only." Modals, drawers and toasts use `shadow-overlay`.
 * What FLOATS beside its trigger — a popover, a select's list, the date picker — uses
 * `shadow-popover` with its mandatory 1px `popover-border` (brief part 3 §C2, ADR 9038). The
 * rest of the app stays flat.
 *
 * G2 §2.6: "No modal dialog for bulk work; modals are for confirmation, never for data
 * entry at volume." That is enforced by shape: `Modal` takes a `confirm` action and a
 * short `bodyKey`, and there is no `children` slot to put a form in. Bulk work goes in
 * a `Drawer`, which is non-modal and leaves the grid visible behind it.
 */

import * as RadixTooltip from '@radix-ui/react-tooltip';
import * as RadixPopover from '@radix-ui/react-popover';
import * as RadixDialog from '@radix-ui/react-dialog';
import * as RadixToast from '@radix-ui/react-toast';
import { CircleCheck, X } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconButton } from './button';
import { FOCUS_RING } from './focus';
import { MOTION } from './motion';

const SURFACE = 'bg-surface-raised border border-hairline rounded-control shadow-overlay';
/** What floats beside its trigger (brief part 3 §C2): 1px ink border in light, #3a2b2b in dark, and the popover shadow. */
const FLOATING = 'bg-surface-raised border border-popover-border rounded-control shadow-popover';

// ── tooltip ──────────────────────────────────────────────────────────────────

export function TooltipProvider({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  return <RadixTooltip.Provider delayDuration={300} skipDelayDuration={0}>{children}</RadixTooltip.Provider>;
}

export interface TooltipProps {
  /** The tooltip's words, as a catalogue key (G6). Give this or `content`. */
  readonly contentKey?: string;
  /**
   * The tooltip's words when they are DATA already in words — the whole of a truncated cell's
   * text (`TruncatedText`), a decision's date and reason — rather than copy. Wins over
   * `contentKey`.
   */
  readonly content?: string;
  readonly children: React.ReactNode;
}

/**
 * A tooltip is supplementary, never the only place information appears — a
 * touch-and-keyboard user may never see it. G2 §2.6: "no mouse-only affordance".
 * Radix opens it on keyboard focus as well as on hover, so its trigger must be focusable.
 *
 * It carries its own `Provider`. Radix's `Tooltip.Root` throws without one, which makes a
 * tooltip a component that works everywhere the app shell has been mounted and nowhere
 * else — including in every unit test of every component that happens to contain one. A
 * primitive whose correctness depends on an ancestor three levels up in another module is a
 * primitive that will be found broken by a chapter agent rather than by us. Nesting
 * providers is supported; the cost is that `skipDelayDuration` is scoped per tooltip
 * rather than shared, which is not a behaviour any screen depends on.
 *
 * The designer's tooltip (brief part 1 §1, pkgC2): an ink fill, white 12px text and a 6px
 * arrow. `ink-primary` under `surface-raised` text is exactly that in light (20.4:1) and
 * inverts with the theme in dark — the off-white #f3efe6 under #1f1414 (15.7:1) — the way
 * the error strip does (§8), so it stays the strongest mark on a dark page too.
 */
export function Tooltip({ contentKey, content, children }: TooltipProps): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <RadixTooltip.Provider delayDuration={300}>
    <RadixTooltip.Root>
      {/* `data-tooltip-trigger` marks the element as one whose words a tooltip completes, so the
          truncation guard (`e2e/pseudo-expansion.spec.ts`, usability audit D) can tell a cut
          text that can be read whole from one that cannot. Radix's Slot puts it on the child. */}
      <RadixTooltip.Trigger asChild data-tooltip-trigger="">{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          sideOffset={4}
          className="bg-ink-primary text-surface-raised text-caption z-popover max-w-xs px-2 py-1"
          data-tooltip=""
        >
          {content ?? (contentKey === undefined ? null : t(contentKey))}
          <RadixTooltip.Arrow width={12} height={6} className="fill-ink-primary" />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}

// ── popover ──────────────────────────────────────────────────────────────────

export interface PopoverProps {
  readonly triggerLabelKey: string;
  readonly trigger?: React.ReactNode;
  readonly children: React.ReactNode;
  readonly align?: 'start' | 'center' | 'end';
}

export function Popover({ triggerLabelKey, trigger, children, align = 'start' }: PopoverProps): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <RadixPopover.Root>
      <RadixPopover.Trigger asChild>
        {trigger ?? <Button variant="secondary">{t(triggerLabelKey)}</Button>}
      </RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content
          align={align}
          sideOffset={4}
          // Never taller than the room Radix measured beside the trigger: a popover near
          // the foot of the rail otherwise ran off the bottom of the window.
          className={`${FLOATING} z-popover max-h-[var(--radix-popover-content-available-height)] min-w-[14rem] overflow-y-auto p-3`}
        >
          {children}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}

// ── modal: confirmation only ─────────────────────────────────────────────────

export interface ModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly titleKey: string;
  /** Short. If this needs to be a form, it is a Drawer (G2 §2.6). */
  readonly bodyKey: string;
  readonly bodyVars?: Record<string, unknown>;
  readonly confirm: {
    readonly labelKey: string;
    readonly onConfirm: () => void;
    readonly destructive?: boolean;
  };
  readonly cancelLabelKey?: string;
  /**
   * design/09 §8: "Every commit action names its scenario and baseline in the
   * confirmation." A commit modal must pass both, and §8's scenario switcher blocks a
   * commit without them.
   */
  readonly commitContext?: { readonly scenario: string; readonly baselineVersion: string };
}

export function Modal({
  open, onOpenChange, titleKey, bodyKey, bodyVars, confirm, cancelLabelKey = 'action.cancel', commitContext,
}: ModalProps): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="z-modal fixed inset-0 bg-scrim" />
        <RadixDialog.Content
          // Centred by both inline edges and auto margins, not `start-1/2 -translate-x-1/2`: that
          // translate is physical and put the dialog half its width off centre in RTL (audit B8).
          className={`${SURFACE} z-modal fixed start-0 end-0 top-1/2 mx-auto max-h-[90dvh] w-[min(32rem,90vw)] -translate-y-1/2 overflow-y-auto p-4`}
        >
          <RadixDialog.Title className="text-section text-ink-primary font-display uppercase">
            {t(titleKey)}
          </RadixDialog.Title>
          <RadixDialog.Description className="text-body text-ink-secondary mt-2">
            {t(bodyKey, bodyVars ?? {})}
          </RadixDialog.Description>
          {commitContext && (
            <p className="text-caption text-ink-secondary bg-surface-sunken mt-3 rounded-chip p-2">
              {t('commit.namesScenarioAndBaseline', commitContext)}
            </p>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <RadixDialog.Close asChild>
              <Button variant="ghost">{t(cancelLabelKey)}</Button>
            </RadixDialog.Close>
            <Button
              variant={confirm.destructive === true ? 'destructive' : 'primary'}
              onClick={() => { confirm.onConfirm(); onOpenChange(false); }}
            >
              {t(confirm.labelKey)}
            </Button>
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

// ── drawer: where bulk work happens ──────────────────────────────────────────

export interface DrawerProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly titleKey: string;
  /** Values for `titleKey`'s ICU placeholders — a title such as «Segna esportati {count}». */
  readonly titleValues?: Readonly<Record<string, string | number>>;
  readonly children: React.ReactNode;
  /** `inline-end` by default — a logical side, never `right` (G6). */
  readonly side?: 'inline-end' | 'block-end';
  readonly closeLabelKey?: string;
}

export function Drawer({
  open, onOpenChange, titleKey, titleValues, children, side = 'inline-end', closeLabelKey = 'action.close',
}: DrawerProps): React.JSX.Element {
  const { t } = useTranslation();
  /*
    These are Tailwind class names, and `inset-block-0` / `inset-inline-end-0` are not
    among them — Tailwind spells the inline axis `start-*` / `end-*` and the block axis
    `top-*` / `bottom-*` / `inset-y-*`. The CSS-property spellings emitted NO RULE, so the
    drawer was a `fixed` box with `inset: auto`, which means its static position: the end
    of `<body>`, below the whole document, 20px tall and against the inline start. It was
    never on screen. `end-*` keeps the inline axis logical, which is the half of G6 that
    matters for RTL; the block axis has no logical Tailwind spelling and `bottom-0` is not
    on `bata/logical-css-only`'s list for that reason.
  */
  // The drawer enters from the side it sits on (designer audit 7.2, 450ms, ADR 9029); under
  // reduced motion it is simply there.
  const position = side === 'inline-end'
    ? `inset-y-0 end-0 w-[min(36rem,90vw)] border-s ${MOTION.slideInEnd}`
    : `start-0 end-0 bottom-0 h-[min(32rem,80vh)] border-t ${MOTION.fadeUp}`;
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange} modal={false}>
      <RadixDialog.Portal>
        <RadixDialog.Content
          className={`bg-surface-raised border-hairline z-modal fixed ${position} flex flex-col shadow-overlay`}
        >
          <header className="border-hairline flex items-center justify-between gap-2 border-b p-3">
            <RadixDialog.Title className="text-section text-ink-primary font-display uppercase">
              {t(titleKey, titleValues ?? {})}
            </RadixDialog.Title>
            <RadixDialog.Close asChild>
              <IconButton variant="ghost" size="sm" icon={<X size={14} aria-hidden />} aria-label={t(closeLabelKey)} />
            </RadixDialog.Close>
          </header>
          <div className="grow overflow-auto p-3">{children}</div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

// ── tour panel: one step of a guided tour ───────────────────────────────────

export interface TourPanelProps {
  /** The step's heading and text, as catalogue keys (G6). */
  readonly titleKey: string;
  readonly bodyKey: string;
  /** Zero-based position of this step, and how many there are. */
  readonly index: number;
  readonly count: number;
  /**
   * The element this step describes is not on the page right now — no run chosen, or its
   * figures have not arrived. The step still reads, with a line saying so, rather than being
   * skipped: a tour that silently loses steps teaches a page that does not exist.
   */
  readonly absent: boolean;
  /** Which edge of the window the panel docks to, so it never covers what it describes. */
  readonly dock: 'block-start' | 'block-end';
  readonly onBack: () => void;
  readonly onNext: () => void;
  readonly onClose: () => void;
}

/**
 * The card a guided tour speaks through (ADR 9044). Docked to a corner of the window rather
 * than floated beside its element: a card positioned against an element has to be repositioned
 * on every scroll, resize and data arrival, and still lands on top of the next thing the planner
 * wanted to read. The element is marked by an outline instead (`src/planning/tour.tsx`).
 *
 * NON-MODAL on purpose: the page stays readable and scrollable behind it, and nothing on the page
 * is pressed on the planner's behalf. Escape and the close button end the tour; the heading takes
 * the focus at every step, so a screen reader hears the step and Tab reaches its buttons first.
 */
export function TourPanel({
  titleKey, bodyKey, index, count, absent, dock, onBack, onNext, onClose,
}: TourPanelProps): React.JSX.Element {
  const { t } = useTranslation();
  const titleId = useId();
  const bodyId = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { heading.current?.focus(); }, [index]);
  const last = index === count - 1;
  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      data-tour-panel=""
      className={`${SURFACE} z-popover fixed end-4 ${dock === 'block-start' ? 'top-4' : 'bottom-4'} flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2 p-4 ${MOTION.fadeUp}`}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose();
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-micro text-ink-secondary">{t('tour.progress', { current: index + 1, count })}</p>
        <IconButton icon={<X size={14} aria-hidden />} aria-label={t('tour.close')} variant="ghost" size="sm" onClick={onClose} />
      </div>
      <h2 ref={heading} id={titleId} tabIndex={-1} className={`text-subsection text-ink-primary rounded-control ${FOCUS_RING}`}>
        {t(titleKey)}
      </h2>
      <div id={bodyId} className="flex flex-col gap-2">
        <p className="text-body text-ink-primary">{t(bodyKey)}</p>
        {absent && <p className="text-caption text-ink-secondary bg-surface-sunken rounded-chip p-2">{t('tour.absent')}</p>}
      </div>
      <div className="mt-2 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} disabled={index === 0}>{t('tour.back')}</Button>
        <Button variant="primary" size="sm" onClick={last ? onClose : onNext}>{t(last ? 'tour.finish' : 'tour.next')}</Button>
      </div>
    </div>
  );
}

// ── toast: the undo surface ──────────────────────────────────────────────────

/**
 * A notice: an outcome the person has already been shown on the page, repeated where the eye
 * is after pressing the action — the designer's audit 3.9 ("toast with the outcome"). It has
 * no action slot: an Undo exists only where the platform can revoke, and no decision write can
 * (audit 3.10), so a notice is only ever words.
 */
export interface Notice {
  readonly titleKey: string;
  readonly titleValues?: Record<string, unknown>;
  readonly descriptionKey?: string;
  readonly descriptionValues?: Record<string, unknown>;
}

/**
 * How long a notice stays: the mockup's own 5.2 seconds. Radix pauses the timer while the
 * pointer or focus is on the toast, and every notice repeats a statement the page keeps, so
 * nothing is lost when it goes. An undo toast keeps the provider's minutes (G2 §2.6).
 */
const NOTICE_DURATION_MS = 5_200;

/** Outside a `ToastProvider` (a unit test of one panel) a notice goes nowhere, by design. */
const NotifyContext = createContext<(notice: Notice) => void>(() => undefined);

/** Show a notice. The page's own statement of the outcome stays; this repeats it. */
export function useNotify(): (notice: Notice) => void {
  return useContext(NotifyContext);
}

export function ToastProvider({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  const [notices, setNotices] = useState<readonly { readonly id: number; readonly notice: Notice }[]>([]);
  const nextId = useRef(0);
  const notify = useCallback((notice: Notice) => {
    nextId.current += 1;
    const id = nextId.current;
    setNotices((shown) => [...shown, { id, notice }]);
  }, []);
  return (
    <RadixToast.Provider
      // G2 §2.6: "Undo for every destructive or bulk action, with a window measured in
      // MINUTES, not seconds." The toast is how the undo is offered, so it must not
      // vanish in four seconds.
      duration={120_000}
      swipeDirection="right"
    >
      <NotifyContext.Provider value={notify}>{children}</NotifyContext.Provider>
      {notices.map(({ id, notice }) => (
        <Toast
          key={id}
          open
          duration={NOTICE_DURATION_MS}
          onOpenChange={(open) => { if (!open) setNotices((shown) => shown.filter((entry) => entry.id !== id)); }}
          titleKey={notice.titleKey}
          {...(notice.titleValues === undefined ? {} : { titleValues: notice.titleValues })}
          {...(notice.descriptionKey === undefined ? {} : { descriptionKey: notice.descriptionKey })}
          {...(notice.descriptionValues === undefined ? {} : { descriptionVars: notice.descriptionValues })}
        />
      ))}
      {/* Same three defects as the drawer above, in one class list: `inset-block-end-4`
          and `inset-inline-end-4` are not Tailwind spellings, and `w-96` asks the token
          spacing scale for a key it does not have (it tops out at 64px). All three emitted
          nothing, so the undo toast — the surface G2 §2.6 makes the whole undo window
          depend on — rendered 33px wide at the foot of the DOCUMENT rather than of the
          window. */}
      <RadixToast.Viewport className="z-toast fixed bottom-4 end-4 flex w-[min(24rem,90vw)] flex-col gap-2" />
    </RadixToast.Provider>
  );
}

export interface ToastProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly titleKey: string;
  readonly titleValues?: Record<string, unknown>;
  readonly descriptionKey?: string;
  readonly descriptionVars?: Record<string, unknown>;
  /** Milliseconds before it closes itself; the provider's minutes when absent. */
  readonly duration?: number;
  /** G2 §2.6 — "everything else is undoable rather than confirmed". */
  readonly undo?: { readonly labelKey: string; readonly onUndo: () => void };
}

export function Toast({
  open, onOpenChange, titleKey, titleValues, descriptionKey, descriptionVars, duration, undo,
}: ToastProps): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <RadixToast.Root
      open={open}
      onOpenChange={onOpenChange}
      {...(duration === undefined ? {} : { duration })}
      className={`${SURFACE} flex items-start gap-3 p-3 ${MOTION.fadeUp}`}
    >
      {/* A notice states an outcome that happened: the check says so, and the title says it in words. */}
      {undo === undefined && <CircleCheck size={16} aria-hidden className="text-status-good mt-[2px] shrink-0" />}
      <div className="flex grow flex-col gap-1">
        <RadixToast.Title className="text-body font-semibold text-ink-primary">{t(titleKey, titleValues ?? {})}</RadixToast.Title>
        {descriptionKey && (
          <RadixToast.Description className="text-caption text-ink-secondary">
            {t(descriptionKey, descriptionVars ?? {})}
          </RadixToast.Description>
        )}
      </div>
      {undo && (
        <RadixToast.Action asChild altText={t(undo.labelKey)}>
          <Button variant="secondary" size="sm" onClick={undo.onUndo} className={FOCUS_RING}>
            {t(undo.labelKey)}
          </Button>
        </RadixToast.Action>
      )}
    </RadixToast.Root>
  );
}
